import React, { useState, useEffect, useMemo, useRef } from "react";
import axiosInstance from "../api/axiosInstance";
import clientAxiosInstance from "../api/clientAxiosInstance";
import { updateHouseholdAPI, validateAccountData } from "../api/updateHouseholdAPI";
import { isSpecialRegistration, isRetirementAccount } from "../utils/formatters";
import DateInput from "./DateInput";
import StateCitySelect from "./StateCitySelect";
import MemberSelect from "./shared/MemberSelect";
import { ToggleField } from "./client/shared/DataField";
import {
  formatPhoneInput,
  validatePhoneDigits,
  formatZipInput,
  validateZipDigits,
  formatSSNInput,
  validateSSNDigits,
} from "../utils/validationHelpers";
import { BFS_MAPPING } from "./HouseholdSetupWizard/WizardConstants";
import {
  WITHDRAWAL_METHOD_OPTIONS,
  DEPOSIT_METHOD_OPTIONS,
  REGISTRATION_OPTIONS,
  getAccountTypesForRegistration,
  PROCEEDS_OPTIONS,
  SECURITY_PURCHASES_OPTIONS,
  DIVIDEND_OPTIONS,
  DEFAULT_DIVIDEND_OPTION,
  TYPE_OF_TRANSFER_OPTIONS,
  FREQUENCY_OPTIONS,
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_NEEDS_MAP,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  LIQUIDITY_TIME_HORIZON_MAP,
  getLiquidityNeedsLabel as getLiquidityNeedsLabelFromRange,
  getLiquidityTimeHorizonLabel as getLiquidityTimeHorizonLabelFromRange,
  getTaxClassificationOptions,
} from "../constants/accountConstants";
import {
  RISK_TOLERANCE_OPTIONS,
  PRIMARY_OBJECTIVE_OPTIONS,
  DECISION_MAKING_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
  PRIMARY_USE_OPTIONS,
  ACCOUNT_TIME_HORIZON_OPTIONS,
  ACCOUNT_TIME_HORIZON_MAP,
  getAccountRiskTimeHorizonLabel,
  parseAccountRiskTimeHorizonDisplay,
} from "../constants/householdConstants";
import { RELATIONSHIP_OPTIONS } from "../constants/membersConstants";
import { runRelationshipPrefill } from "../hooks/useRelationshipPrefill";
import {
  computeBrokerageFlags,
  showSourceOfFunds,
  showUseOfFunds,
  showLiquidityRequirements,
  showAccountRiskDetails,
} from "../utils/brokerageFields";

const getTimeHorizonLabel = (th) => getAccountRiskTimeHorizonLabel(th, "");

const getLiquidityNeedsLabel = (amount) =>
  getLiquidityNeedsLabelFromRange(amount, "None");

const getLiquidityTimeHorizonLabel = (th) =>
  getLiquidityTimeHorizonLabelFromRange(th, "None");

// ─── Default form data ────────────────────────────────────────────────────────

const DEFAULT_RISK = {
  risk_tolerance: "Low",
  invest_objectives: [PRIMARY_OBJECTIVE_OPTIONS[0]],
  time_horizon_label: ACCOUNT_TIME_HORIZON_OPTIONS[0],
  decision_making: DECISION_MAKING_OPTIONS[0],
  source_of_funds: [SOURCE_OF_FUNDS_OPTIONS[0]],
  use_of_funds: PRIMARY_USE_OPTIONS[1],
  liquidity_needs: "",
  liquidity_time_horizon: "",
};

const DEFAULT_FORM = {
  registration: "Individual",
  custodian: "Fidelity",
  type: "Individual",
  new_account_number: "",
  account_value: "",
  fee_based_account: true,
  margin: false,
  options: false,
  is_planned: true,
  transfer_extent: "Full",
  transfer_type: "Brokerage or Trust Company",
  proceeds: "Hold in Core Account Investment Vehicle",
  security_purchases: "Hold in Street Name",
  distribution_income: DEFAULT_DIVIDEND_OPTION,
  risk_tolerance: { ...DEFAULT_RISK },
};

/**
 * Map API `investment_strategy_defaults` or account `risk_tolerance` → form state.
 */
function mapApiRiskToleranceToFormState(apiRt) {
  if (apiRt == null || typeof apiRt !== "object") return null;
  const rt = { ...DEFAULT_RISK };
  rt.risk_tolerance = apiRt.risk_tolerance || "Low";
  rt.invest_objectives = Array.isArray(apiRt.invest_objectives)
    ? apiRt.invest_objectives
    : apiRt.invest_objectives
      ? [apiRt.invest_objectives]
      : [PRIMARY_OBJECTIVE_OPTIONS[0]];
  rt.time_horizon_label =
    getTimeHorizonLabel(apiRt.time_horizon) || ACCOUNT_TIME_HORIZON_OPTIONS[0];
  rt.decision_making =
    apiRt.decision_making || "Consults with investment professional";
  rt.source_of_funds = Array.isArray(apiRt.source_of_funds)
    ? apiRt.source_of_funds
    : apiRt.source_of_funds
      ? [apiRt.source_of_funds]
      : [SOURCE_OF_FUNDS_OPTIONS[0]];
  rt.use_of_funds = apiRt.use_of_funds || PRIMARY_USE_OPTIONS[0];
  rt.liquidity_needs = getLiquidityNeedsLabel(
    apiRt.liquidity_requirements?.amount,
  );
  rt.liquidity_time_horizon = getLiquidityTimeHorizonLabel(
    apiRt.liquidity_requirements?.time_horizon,
  );
  return rt;
}

/** Ensure API-only labels (not in static option lists) still render in <select>. */
function mergeSelectOptions(options, currentValue) {
  if (currentValue == null) return options;
  const v =
    typeof currentValue === "string" ? currentValue.trim() : String(currentValue);
  if (v === "") return options;
  if (options.includes(v)) return options;
  return [v, ...options];
}

const DEFAULT_OWNER = {
  mode: "existing",
  id: "",
  first_name: "",
  last_name: "",
  email: "",
  relationship: "",
};

// ─── UI Primitives ────────────────────────────────────────────────────────────

const SectionHeader = ({ num, children, color = "emerald" }) => {
  const bar = {
    emerald: "bg-emerald-500",
    purple: "bg-purple-500",
    blue: "bg-blue-500",
  }[color];
  return (
    <div className="flex items-center gap-2 mb-5">
      <div className={`w-1 h-5 ${bar} rounded-full`} />
      {num ? (
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
          <span className="text-gray-400 mr-1">{num}.</span>
          {children}
        </h3>
      ) : (
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
          {children}
        </h3>
      )}
    </div>
  );
};

const SubHeader = ({ children }) => (
  <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4 border-b border-gray-100 pb-2">
    {children}
  </h5>
);

/** Yes / No (or custom label) toggle pill — exactly h-10 to match adjacent inputs */
const YesNo = ({
  value,
  onTrue,
  onFalse,
  trueLabel = "Yes",
  falseLabel = "No",
  color = "emerald",
}) => {
  const active = {
    emerald: "bg-emerald-600 text-white shadow-sm",
    purple: "bg-purple-600 text-white shadow-sm",
    blue: "bg-blue-600 text-white shadow-sm",
  }[color];
  return (
    <div className="flex h-10 bg-gray-50 rounded-lg p-0.5 border border-gray-200 w-full">
      <button
        type="button"
        onClick={onTrue}
        className={`flex-1 flex items-center justify-center rounded-md text-sm font-bold transition-all ${value === true ? active : "text-gray-500 hover:bg-white/60"}`}
      >
        {trueLabel}
      </button>
      <button
        type="button"
        onClick={onFalse}
        className={`flex-1 flex items-center justify-center rounded-md text-sm font-bold transition-all ${value === false ? active : "text-gray-500 hover:bg-white/60"}`}
      >
        {falseLabel}
      </button>
    </div>
  );
};

/** Select with custom chevron */
const Sel = ({ value, onChange, options, placeholder, disabled }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full h-10 px-3 pr-9 border border-gray-200 rounded-lg text-sm appearance-none bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none disabled:bg-gray-50 disabled:text-gray-500"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </div>
  </div>
);

/** Checkbox card (emerald border when checked) */
const FeatureCard = ({ label, description, checked, onChange }) => (
  <label
    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${checked ? "border-emerald-500 bg-emerald-50" : "border-gray-100 hover:border-emerald-200 bg-white"}`}
  >
    <div
      className={`w-6 h-6 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${checked ? "bg-emerald-500 border-emerald-500" : "bg-white border-gray-300"}`}
    >
      {checked && (
        <svg
          className="w-4 h-4 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
          />
        </svg>
      )}
    </div>
    <input
      type="checkbox"
      className="hidden"
      checked={checked}
      onChange={onChange}
    />
    <div>
      <span
        className={`font-bold block text-sm ${checked ? "text-emerald-800" : "text-gray-700"}`}
      >
        {label}
      </span>
      {description && (
        <span className="text-xs text-gray-500">{description}</span>
      )}
    </div>
  </label>
);

// ─── OwnerSlot ────────────────────────────────────────────────────────────────

const OwnerSlot = ({ label, owner, onChange, members, required = false }) => {
  const isNew = owner.mode === "new";
  const lbl = "block text-xs font-medium text-gray-500 mb-1";
  const inp =
    "w-full h-9 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none";

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">{label}</span>
        <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
          {["existing", "new"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onChange({ ...DEFAULT_OWNER, mode: m })}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${owner.mode === m ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {m === "existing" ? "Existing Member" : "New Member"}
            </button>
          ))}
        </div>
      </div>

      {isNew ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>
                First Name {required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                placeholder="First name"
                value={owner.first_name}
                onChange={(e) =>
                  onChange({ ...owner, first_name: e.target.value })
                }
                className={inp}
              />
            </div>
            <div>
              <label className={lbl}>
                Last Name {required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                placeholder="Last name"
                value={owner.last_name}
                onChange={(e) =>
                  onChange({ ...owner, last_name: e.target.value })
                }
                className={inp}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>
                Email {required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="email"
                placeholder="email@example.com"
                value={owner.email}
                onChange={(e) => onChange({ ...owner, email: e.target.value })}
                className={inp}
              />
            </div>
            <div>
              <label className={lbl}>
                Relationship <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={owner.relationship}
                  onChange={(e) =>
                    onChange({ ...owner, relationship: e.target.value })
                  }
                  className="w-full h-9 px-3 pr-9 border border-gray-200 rounded-lg text-sm appearance-none bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                >
                  <option value="">Select…</option>
                  {RELATIONSHIP_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <label className={lbl}>
            Select Member {required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={owner.id}
            onChange={(e) => onChange({ ...owner, id: e.target.value })}
            className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
          >
            <option value="">Select member…</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.personal_info?.first_name} {m.personal_info?.last_name}
                {m.personal_info?.email ? ` — ${m.personal_info.email}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AddAccountModal = ({
  isOpen,
  onClose,
  household,
  clientToken,
  onAccountAdded,
  custodianTypes,
  brokerageFlags: brokerageFlagsProp,
}) => {
  const isFeeBasedEditable =
    !custodianTypes ||
    (custodianTypes.iws === true && custodianTypes.nfs === true);
  const brokerageFlags = brokerageFlagsProp ?? computeBrokerageFlags({
    custodian: household?.custodian,
    custodianTypes: custodianTypes ?? household?.custodian_types,
    destinationFirm: household?.destination_firm,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [owner1, setOwner1] = useState({ ...DEFAULT_OWNER });
  const [additionalOwners, setAdditionalOwners] = useState([]);

  const [formData, setFormData] = useState(
    JSON.parse(JSON.stringify(DEFAULT_FORM)),
  );
  const [useHouseholdDefaults, setUseHouseholdDefaults] = useState(true);
  const [addTransactions, setAddTransactions] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  // Derived special-registration flags (always in sync with formData.registration)
  const isSpecialReg = isSpecialRegistration(formData.registration);
  const isBusinessReg = formData.registration?.toLowerCase() === "business";
  const isInheritedReg = formData.registration === "Inherited Retirement";



  const [fieldErrors, setFieldErrors] = useState({});
  const handleFieldError = (field, msg) => {
    setFieldErrors((prev) => ({ ...prev, [field]: msg }));
  };

  const DEFAULT_OTHER_INFO = { note: "" };
  const DEFAULT_BUSINESS_INFO = {
    business_name: "",
    tax_classification: "",
    is_operating: true,
    is_non_profit: false,
    address: { address_line_1: "", address_line_2: "", country: "USA", province: "", city: "", postal_code: "", phone: "" },
    control_persons: [],
    ssn: "",
    notes: "",
  };
  const [otherInfo, setOtherInfo] = useState({ ...DEFAULT_OTHER_INFO });
  const [businessInfo, setBusinessInfo] = useState({ ...DEFAULT_BUSINESS_INFO });
  const [decedentInfo, setDecedentInfo] = useState({ id: "", relationship: "" });

  const autoFilledDecedentRelationship = useRef("");
  const decedentOwnerId = owner1.mode === "existing" ? owner1.id : "";
  const decedentExcludeIds = useMemo(
    () => (decedentOwnerId ? [decedentOwnerId] : []),
    [decedentOwnerId],
  );

  useEffect(() => {
    if (!isInheritedReg || !household?.id) return undefined;
    if (!decedentOwnerId || !decedentInfo.id || decedentOwnerId === decedentInfo.id) return undefined;
    let cancelled = false;
    runRelationshipPrefill({
      householdId: household.id,
      memberId: decedentOwnerId,
      counterpartyMemberId: decedentInfo.id,
      clientToken,
      getCurrentValue: () =>
        decedentInfo.relationship === autoFilledDecedentRelationship.current
          ? ""
          : decedentInfo.relationship,
      onPrefill: (relationship) => {
        if (cancelled) return;
        autoFilledDecedentRelationship.current = relationship;
        setDecedentInfo((p) => ({ ...p, relationship }));
      },
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInheritedReg, household?.id, decedentOwnerId, decedentInfo.id, clientToken]);

  const accountTypeOptions = useMemo(
    () => getAccountTypesForRegistration(formData.registration),
    [formData.registration],
  );

  // Reset type when registration changes (default to first option so state
  // matches what the <select> visually shows — prevents empty formData.type
  // when the user hasn't interacted with the type dropdown)
  useEffect(() => {
    if (accountTypeOptions.length > 0) {
      set("type", accountTypeOptions[0]);
    } else {
      set("type", "");
    }
  }, [formData.registration]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBusinessInfo((p) =>
      getTaxClassificationOptions(formData.type).includes(p.tax_classification)
        ? p
        : { ...p, tax_classification: "" },
    );
  }, [formData.type]);

  const isJoint = formData.registration?.toLowerCase().includes("joint");
  const ownershipLimits = useMemo(() => {
    const reg = formData.registration || "";
    if (reg === "Joint" || reg === "UTMA/UGMA") return { min: 2, max: 2 };
    if (["Individual", "529"].includes(reg) || reg.includes("Retirement") || reg.includes("IRA")) {
      return { min: 1, max: 1 };
    }
    if (reg === "Business") {
      return { min: 1, max: 4 };
    }
    if (reg === "Trust") {
      return { min: 1, max: 2 };
    }
    return { min: 1, max: 2 };
  }, [formData.registration]);

  const members = household?.members || [];

  const isIws = household?.is_fee_standard !== false;
  const showEstimatedAccountValue = formData.fee_based_account === false;

  /** Stable key so we re-init when strategy defaults / first account load async (without resetting on unrelated household edits). */
  const investmentStrategyInitKey = useMemo(() => {
    const hhAccounts = household?.accounts;
    const firstAccount = hhAccounts
      ? Array.isArray(hhAccounts)
        ? hhAccounts[0]
        : Object.values(hhAccounts)[0]
      : null;
    return JSON.stringify({
      strategyDefaults: household?.investment_strategy_defaults ?? null,
      accountRt: firstAccount?.risk_tolerance ?? null,
    });
  }, [household?.accounts, household?.investment_strategy_defaults]);

  const membersInitKey = useMemo(
    () => (household?.members ?? []).map((m) => m.id).join(","),
    [household?.members],
  );

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    // Prefer household `investment_strategy_defaults`; else first account's risk_tolerance
    const hhAccounts = household?.accounts;
    const firstAccount = hhAccounts
      ? Array.isArray(hhAccounts)
        ? hhAccounts[0]
        : Object.values(hhAccounts)[0]
      : null;
    const strategyDefaults = household?.investment_strategy_defaults;
    const rt =
      (strategyDefaults != null
        ? mapApiRiskToleranceToFormState(strategyDefaults)
        : null) ??
      mapApiRiskToleranceToFormState(firstAccount?.risk_tolerance) ?? {
        ...DEFAULT_RISK,
      };

    const feeBasedDefault = !isFeeBasedEditable
      ? custodianTypes?.nfs === true && custodianTypes?.iws !== true
        ? false
        : true
      : true;
    setFormData({
      ...JSON.parse(JSON.stringify(DEFAULT_FORM)),
      risk_tolerance: rt,
      fee_based_account: feeBasedDefault,
    });
    setOwner1({
      ...DEFAULT_OWNER,
      id: (members.find((m) => m.is_primary) || members[0])?.id || "",
    });
    setAdditionalOwners([]);
    setUseHouseholdDefaults(true);
    setAddTransactions(false);
    setDeposits([]);
    setWithdrawals([]);
    setOtherInfo({ note: "" });
    setBusinessInfo(JSON.parse(JSON.stringify(DEFAULT_BUSINESS_INFO)));
    autoFilledDecedentRelationship.current = "";
    setDecedentInfo({ id: "", relationship: "" });
    setFieldErrors({});
    setError("");
  }, [
    isOpen,
    investmentStrategyInitKey,
    membersInitKey,
    isFeeBasedEditable,
    custodianTypes?.iws,
    custodianTypes?.nfs,
  ]);

  // Auto-show/hide owner 2 based on ownership limits
  useEffect(() => {
    if (ownershipLimits.min === 2) {
      setAdditionalOwners((prev) => (prev.length > 0 ? prev : [{ ...DEFAULT_OWNER }]));
    } else if (ownershipLimits.max === 1) {
      setAdditionalOwners([]);
    } else {
      setAdditionalOwners((prev) => prev.slice(0, ownershipLimits.max - 1));
    }
  }, [ownershipLimits]);

  useEffect(() => {
    if (isOpen && ownershipLimits.max === 1 && additionalOwners.length === 0) {
      const primaryMember = members.find((m) => m.is_primary);
      if (primaryMember && !owner1.id && owner1.mode === "existing") {
        setOwner1((prev) => ({ ...prev, id: primaryMember.id }));
      }
    }
  }, [isOpen, ownershipLimits.max, additionalOwners.length, members, owner1.id, owner1.mode]);

  // ── Field setters ──────────────────────────────────────────────────────────
  const set = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const setRt = (field, value) =>
    setFormData((p) => ({
      ...p,
      risk_tolerance: { ...p.risk_tolerance, [field]: value },
    }));

  const handleObjectiveChange = (objective) => {
    const matchingKey = Object.keys(BFS_MAPPING).find(
      (key) => BFS_MAPPING[key].invest_objectives?.[0] === objective,
    );
    if (matchingKey) {
      const defaults = BFS_MAPPING[matchingKey];
      const rawSources = Array.isArray(defaults.source_of_funds)
        ? defaults.source_of_funds
        : [defaults.source_of_funds];
      setFormData((p) => ({
        ...p,
        risk_tolerance: {
          ...p.risk_tolerance,
          invest_objectives: [objective],
          risk_tolerance: defaults.risk_tolerance,
          time_horizon_label: getTimeHorizonLabel(defaults.time_horizon),
          decision_making: defaults.decision_making,
          source_of_funds: rawSources,
          use_of_funds: defaults.use_of_funds,
        },
      }));
    } else {
      setRt("invest_objectives", objective ? [objective] : []);
    }
  };

  // ── Resolve owner (create new member if needed) ────────────────────────────
  const resolveOwner = async (owner, isAdvisorMode) => {
    if (owner.mode === "existing") return owner.id || null;
    if (
      !owner.first_name ||
      !owner.last_name ||
      !owner.email ||
      !owner.relationship
    ) {
      throw new Error(
        "Please fill in First Name, Last Name, Email and Relationship for all new members.",
      );
    }
    const res = await (
      isAdvisorMode ? axiosInstance : clientAxiosInstance
    ).post(
      isAdvisorMode
        ? `/households/${household.id}/members`
        : `/client/household/${household.id}/members`,
      {
        personal_info: {
          first_name: owner.first_name,
          last_name: owner.last_name,
          email: owner.email,
          relationship: owner.relationship,
        },
      },
      isAdvisorMode
        ? undefined
        : { headers: { Authorization: `Bearer ${clientToken}` } },
    );
    return res.data.id;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    const isAdvisorMode = !clientToken;

    try {
      if (!household?.id)
        throw new Error(
          "Household ID is missing. Please refresh and try again.",
        );

      if (!formData.type) throw new Error("Account Type is required.");

      const isNonProfitBusiness = isBusinessReg && businessInfo.is_non_profit === true;
      const id1 = await resolveOwner(owner1, isAdvisorMode);
      if (!id1 && !isNonProfitBusiness) throw new Error("Primary owner is required.");

      const ownerIds = id1 ? [id1] : [];
      for (let i = 0; i < additionalOwners.length; i++) {
        const extraId = await resolveOwner(additionalOwners[i], isAdvisorMode);
        if (!extraId) throw new Error(`Owner ${i + 2} selection is required.`);
        if (ownerIds.includes(extraId))
          throw new Error("Each owner must be a different member.");
        ownerIds.push(extraId);
      }
      const evenShare = Math.floor(10000 / ownerIds.length) / 100;
      const firstShare = Math.round((100 - evenShare * (ownerIds.length - 1)) * 100) / 100;
      const owners_info = ownerIds.map((id, index) => ({
        id,
        type: "primary",
        share: index === 0 ? firstShare : evenShare,
      }));

      const raw = JSON.parse(JSON.stringify(formData));

      if (useHouseholdDefaults) {
        const hhAccounts = household?.accounts;
        const firstAccount = hhAccounts
          ? Array.isArray(hhAccounts)
            ? hhAccounts[0]
            : Object.values(hhAccounts)[0]
          : null;
        const strategyDefaults = household?.investment_strategy_defaults;
        raw.risk_tolerance =
          (strategyDefaults != null
            ? mapApiRiskToleranceToFormState(strategyDefaults)
            : null) ??
          mapApiRiskToleranceToFormState(firstAccount?.risk_tolerance) ?? {
            ...DEFAULT_RISK,
          };
      }

      if (!isSpecialReg) {
        // Build transfer_info from transfer_extent/transfer_type
        raw.transfer_info = {
          is_full_transfer: raw.transfer_extent !== "Partial",
          transfer_type: raw.transfer_type,
        };

        // Map risk tolerance string dropdowns → numeric ranges
        const rt = raw.risk_tolerance;
        rt.time_horizon =
          ACCOUNT_TIME_HORIZON_MAP[rt.time_horizon_label] ||
          parseAccountRiskTimeHorizonDisplay(rt.time_horizon_label) || {
            start: 0,
            end: 0,
          };
        rt.liquidity_requirements = {
          amount: LIQUIDITY_NEEDS_MAP[rt.liquidity_needs] || { start: 0, end: 0 },
          time_horizon: LIQUIDITY_TIME_HORIZON_MAP[rt.liquidity_time_horizon] || {
            start: 0,
            end: 0,
          },
        };
        delete rt.time_horizon_label;
        delete rt.liquidity_needs;
        delete rt.liquidity_time_horizon;

        // Attach contributions/withdrawals if added
        if (addTransactions) {
          raw.deposits = deposits;
          raw.withdrawals = withdrawals;
        }
      } else {
        // For special registrations strip transfer/investment fields
        delete raw.risk_tolerance;
        delete raw.transfer_extent;
        delete raw.transfer_type;
        delete raw.proceeds;
        delete raw.security_purchases;
        delete raw.distribution_income;
        delete raw.margin;
        delete raw.options;
      }

      if (isBusinessReg) {
        if (!businessInfo.business_name?.trim())
          throw new Error("Business Name is required for Business accounts.");
        const taxOptions = getTaxClassificationOptions(formData.type);
        if (taxOptions.length > 0 && businessInfo.tax_classification && !taxOptions.includes(businessInfo.tax_classification))
          throw new Error("Invalid tax classification for this business type.");
        if (fieldErrors.business_ssn) throw new Error(`Business Tax ID: ${fieldErrors.business_ssn}`);
        if (fieldErrors.business_phone) throw new Error(`Business Phone: ${fieldErrors.business_phone}`);

        const clean = JSON.parse(JSON.stringify(businessInfo));
        ["business_name", "tax_classification", "ssn", "notes"].forEach((k) => {
          if (clean[k] === "") clean[k] = null;
        });
        Object.keys(clean.address).forEach((k) => {
          if (clean.address[k] === "") clean.address[k] = null;
        });
        if (taxOptions.length === 0) clean.tax_classification = null;
        clean.control_persons = (clean.control_persons || []).filter((c) => c && c.id);
        raw.business_info = clean;
      }

      if (isInheritedReg && decedentInfo.id) {
        raw.inheritance_info = {
          decedent_info: {
            id: decedentInfo.id,
            relationship: decedentInfo.relationship || null,
          },
        };
      }

      delete raw.transfer_extent;
      delete raw.transfer_type;

      // Coerce numerics
      const toNum = (v) =>
        v === "" || v === null || v === undefined
          ? null
          : (parseFloat(v) ?? null);
      const skipAccountValue =
        (custodianTypes?.iws === true && custodianTypes?.nfs !== true) ||
        raw.fee_based_account === true;
      raw.account_value = skipAccountValue ? null : toNum(raw.account_value);

      // Clean empty strings
      ["type", "new_account_number"].forEach((f) => {
        if (raw[f] === "") raw[f] = null;
      });

      const newAccount = { ...raw, owners_info, status: "active" };

      // Validate on frontend before sending
      const validationErrors = validateAccountData(newAccount, members);
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(" "));
      }

      let updatedHousehold = null;
      if (isAdvisorMode) {
        const res = await axiosInstance.post(
          `/households/${household.id}/accounts`,
          newAccount,
        );
        updatedHousehold = res.data;
      } else {
        const tempId = `new_${Date.now()}`;
        const result = await updateHouseholdAPI(household.id, clientToken, {
          accounts: { [tempId]: newAccount },
        });
        if (!result.success)
          throw new Error(result.error || "Failed to add account.");
        updatedHousehold = result.data;
      }

      onAccountAdded(updatedHousehold);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg;
      if (Array.isArray(detail)) {
        errorMsg = detail.map((e) => e.msg || JSON.stringify(e)).join("; ");
      } else if (typeof detail === "string") {
        errorMsg = detail;
      } else {
        errorMsg = err.message || "An unexpected error occurred.";
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const lbl = "block text-xs font-medium text-gray-500 mb-1";
  const inp =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white";
  const rt = formData.risk_tolerance;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add New Account</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isSpecialReg
                ? "Configure account details and additional information"
                : "Configure transfer, contributions, and investment details"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-8">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2">
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          )}

          {/* ══ Account Registration ══ */}
          <section>
            <SectionHeader>Account Registration</SectionHeader>
            <div className="flex gap-4">
              <div className="max-w-xs flex-1">
                <label className={lbl}>
                  Registration Type <span className="text-red-500">*</span>
                </label>
                <Sel
                  value={formData.registration}
                  onChange={(e) => set("registration", e.target.value)}
                  options={REGISTRATION_OPTIONS}
                />
              </div>
              <div className="max-w-xs flex-1">
                <label className={lbl}>
                  Type <span className="text-red-500">*</span>
                </label>
                <Sel
                  value={formData.type}
                  onChange={(e) => set("type", e.target.value)}
                  options={accountTypeOptions}
                  disabled={accountTypeOptions.length <= 1}
                />
              </div>
            </div>
          </section>

          {/* ══ Account Owners ══ */}
          <section>
            <SectionHeader>Account Owners</SectionHeader>
            <div className="space-y-3">
              <OwnerSlot
                label="Primary Owner"
                owner={owner1}
                onChange={setOwner1}
                members={members}
                required
              />
              {ownershipLimits.max > 1 && (
                <>
                  {additionalOwners.map((extraOwner, index) => (
                    <div key={index} className="relative">
                      <OwnerSlot
                        label={`Owner ${index + 2}`}
                        owner={extraOwner}
                        onChange={(next) =>
                          setAdditionalOwners((prev) =>
                            prev.map((o, i) => (i === index ? next : o)),
                          )
                        }
                        members={members}
                        required={index < ownershipLimits.min - 1}
                      />
                      {index >= ownershipLimits.min - 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setAdditionalOwners((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                          className="absolute top-4 right-4 text-xs text-gray-400 hover:text-red-500 transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  {additionalOwners.length + 1 < ownershipLimits.max && (
                    <button
                      type="button"
                      onClick={() =>
                        setAdditionalOwners((prev) => [...prev, { ...DEFAULT_OWNER }])
                      }
                      className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-xs font-semibold text-gray-500 hover:border-emerald-400 hover:text-emerald-600 transition-all flex items-center justify-center gap-1.5"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Owner
                    </button>
                  )}
                </>
              )}
            </div>
          </section>

          {/* ══ Business Info (Business registration only) ══ */}
          {isBusinessReg && (
            <section>
              <SectionHeader>Business Info</SectionHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={lbl}>
                    Business Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={businessInfo.business_name}
                    onChange={(e) => setBusinessInfo((p) => ({ ...p, business_name: e.target.value }))}
                    className={inp}
                    placeholder="Legal business name"
                  />
                </div>
                {getTaxClassificationOptions(formData.type).length > 0 && (
                  <div>
                    <label className={lbl}>Tax Classification</label>
                    <Sel
                      value={businessInfo.tax_classification}
                      onChange={(e) => setBusinessInfo((p) => ({ ...p, tax_classification: e.target.value }))}
                      options={getTaxClassificationOptions(formData.type)}
                      placeholder="Select classification..."
                    />
                  </div>
                )}
                <div>
                  <label className={lbl}>Is the business operating?</label>
                  <div className="relative">
                    <select
                      value={businessInfo.is_operating === null ? "" : String(businessInfo.is_operating)}
                      onChange={(e) =>
                        setBusinessInfo((p) => ({
                          ...p,
                          is_operating: e.target.value === "" ? null : e.target.value === "true",
                        }))
                      }
                      className="w-full h-10 px-3 pr-9 border border-gray-200 rounded-lg text-sm appearance-none bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
                <ToggleField
                  label="Is this a non-profit organization?"
                  value={businessInfo.is_non_profit === true}
                  isEditable
                  isEditing
                  fieldPath="is_non_profit"
                  formData={businessInfo}
                  onFieldChange={(fp, v) => setBusinessInfo((p) => ({ ...p, [fp]: v }))}
                  variant="field"
                />
                <div>
                  <label className={lbl}>Business Tax ID (EIN)</label>
                  <input
                    type="text"
                    value={formatSSNInput(businessInfo.ssn)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").substring(0, 9);
                      setBusinessInfo((p) => ({ ...p, ssn: digits }));
                      handleFieldError(
                        "business_ssn",
                        digits && digits.length !== 9 ? "Tax ID must be 9 digits." : "",
                      );
                    }}
                    className={inp}
                    placeholder="XX-XXXXXXX"
                  />
                  {fieldErrors.business_ssn && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.business_ssn}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <MemberSelect
                    multi
                    label="Control Persons"
                    members={members}
                    value={businessInfo.control_persons}
                    onChange={(next) => setBusinessInfo((p) => ({ ...p, control_persons: next }))}
                    addLabel="+ Add Control Person"
                    maxEntries={4}
                  />
                </div>
                <div className="md:col-span-2 space-y-4">
                  <label className={lbl}>Business Address</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className={lbl}>Address Line 1</label>
                      <input
                        type="text"
                        value={businessInfo.address.address_line_1}
                        onChange={(e) => setBusinessInfo((p) => ({ ...p, address: { ...p.address, address_line_1: e.target.value } }))}
                        className={inp}
                      />
                    </div>
                    <div>
                      <label className={lbl}>Address Line 2</label>
                      <input
                        type="text"
                        value={businessInfo.address.address_line_2}
                        onChange={(e) => setBusinessInfo((p) => ({ ...p, address: { ...p.address, address_line_2: e.target.value } }))}
                        className={inp}
                      />
                    </div>
                  </div>
                  <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                    <StateCitySelect
                      countryValue={businessInfo.address.country}
                      stateValue={businessInfo.address.province}
                      cityValue={businessInfo.address.city}
                      onCountryChange={(val) =>
                        setBusinessInfo((p) => ({ ...p, address: { ...p.address, country: val, province: "", city: "" } }))
                      }
                      onStateChange={(val) =>
                        setBusinessInfo((p) => ({ ...p, address: { ...p.address, province: val } }))
                      }
                      onCityChange={(val) =>
                        setBusinessInfo((p) => ({ ...p, address: { ...p.address, city: val } }))
                      }
                      isEditing={true}
                      stateLabel="Province/State"
                      cityLabel="City"
                      zipValue={businessInfo.address.postal_code}
                      onZipChange={(val) =>
                        setBusinessInfo((p) => ({ ...p, address: { ...p.address, postal_code: val } }))
                      }
                      zipLabel="Postal Code"
                      zipRestrictUsFive
                    />
                  </div>
                  <div className="max-w-xs">
                    <label className={lbl}>Business Phone</label>
                    <input
                      type="text"
                      value={formatPhoneInput(businessInfo.address.phone || "")}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").substring(0, 10);
                        setBusinessInfo((p) => ({ ...p, address: { ...p.address, phone: digits } }));
                        handleFieldError(
                          "business_phone",
                          digits && digits.length !== 10 ? "Phone must be 10 digits." : "",
                        );
                      }}
                      className={inp}
                      placeholder="XXX-XXX-XXXX"
                    />
                    {fieldErrors.business_phone && (
                      <p className="text-xs text-red-500 mt-1">{fieldErrors.business_phone}</p>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {isInheritedReg && (
            <section>
              <SectionHeader>Decedent</SectionHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <MemberSelect
                  label="Decedent"
                  members={members}
                  value={decedentInfo.id}
                  excludeIds={decedentExcludeIds}
                  onChange={(id) => setDecedentInfo((p) => ({ ...p, id }))}
                />
                <div>
                  <label className={lbl}>Owner's Relationship to Decedent</label>
                  <Sel
                    value={decedentInfo.relationship}
                    onChange={(e) => {
                      autoFilledDecedentRelationship.current = "";
                      setDecedentInfo((p) => ({ ...p, relationship: e.target.value }));
                    }}
                    options={RELATIONSHIP_OPTIONS}
                    placeholder="Select relationship..."
                  />
                </div>
              </div>
            </section>
          )}

          {/* ══ Other Info (all special registrations) ══ */}
          {/*
          {isSpecialReg && (
            <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-5">
              <SectionHeader num={isBusinessReg ? "3" : "2"} color="emerald">Other Info</SectionHeader>



              // Note
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={lbl}>Note</label>
                  <span className={`text-[11px] tabular-nums ${otherInfo.note.length >= 950 ? "text-red-500 font-semibold" : "text-gray-400"}`}>
                    {otherInfo.note.length}/1000
                  </span>
                </div>
                <textarea value={otherInfo.note}
                  onChange={e => setOtherInfo(p => ({ ...p, note: e.target.value }))}
                  maxLength={1000} rows={4}
                  placeholder="Add notes about this account"
                  className="w-3/5 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 resize-y overflow-y-auto shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/25 placeholder:text-gray-400 min-h-[100px] max-h-[280px]"
                />
              </div>
            </section>
          )}
          */}

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* 1. Transfer & Features — standard registrations only              */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          {!isSpecialReg && (
            <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-7">
              <SectionHeader num="1" color="emerald">
                Transfer &amp; Features
              </SectionHeader>

            {/* A: Account & Transfer Details */}
            <div>
              <SubHeader>Account &amp; Transfer Details</SubHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Show Fee Based Account toggle only if the project allows both IWS and NFS */}
                {isFeeBasedEditable && (
                  <div>
                    <label className={lbl}>Is this a fee-based account?</label>
                    <div className="flex bg-gray-100 rounded-lg p-1 w-full">
                      <button
                        type="button"
                        onClick={() => set("fee_based_account", true)}
                        className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                          formData.fee_based_account === true
                            ? "bg-white text-emerald-700 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => set("fee_based_account", false)}
                        className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                          formData.fee_based_account === false
                            ? "bg-white text-emerald-700 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>
                )}

                {/* Account value only for NFS / non-fee-based (not IWS-only projects, not fee-based accounts) */}
                {showEstimatedAccountValue && (
                <div>
                  <label className={lbl}>Estimated Account Value</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      $
                    </span>
                    <input
                      type="number"
                      min={0}
                      placeholder="0.00"
                      value={formData.account_value}
                      onChange={(e) => set("account_value", e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      className={`${inp} pl-7`}
                    />
                  </div>
                </div>
                )}

                {/* Transfer Extent */}
                <div>
                  <label className={lbl}>Transfer Extent</label>
                  <YesNo
                    value={formData.transfer_extent === "Full"}
                    onTrue={() => set("transfer_extent", "Full")}
                    onFalse={() => set("transfer_extent", "Partial")}
                    trueLabel="Full"
                    falseLabel="Partial"
                  />
                </div>

                {/* Transfer Type */}
                <div>
                  <label className={lbl}>Transfer Type</label>
                  <Sel
                    value={formData.transfer_type}
                    onChange={(e) => set("transfer_type", e.target.value)}
                    options={TYPE_OF_TRANSFER_OPTIONS}
                  />
                </div>
              </div>
            </div>

            {/* B: Standing Instructions */}
            <div>
              <SubHeader>Standing Instructions</SubHeader>
              <div className="space-y-5">
                <div>
                  <label className={lbl}>How should proceeds be handled?</label>
                  <Sel
                    value={formData.proceeds}
                    onChange={(e) => set("proceeds", e.target.value)}
                    options={PROCEEDS_OPTIONS}
                  />
                </div>
                <div>
                  <label className={lbl}>
                    How should security purchases be handled?
                  </label>
                  <Sel
                    value={formData.security_purchases}
                    onChange={(e) => set("security_purchases", e.target.value)}
                    options={SECURITY_PURCHASES_OPTIONS}
                  />
                </div>
                <div>
                  <label className={lbl}>
                    How should dividend/distribution income be handled?
                  </label>
                  <Sel
                    value={formData.distribution_income}
                    onChange={(e) => set("distribution_income", e.target.value)}
                    options={DIVIDEND_OPTIONS}
                  />
                </div>
              </div>
            </div>

            {/* C: Account Features */}
            <div>
              <SubHeader>Account Features</SubHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isRetirementAccount(formData.registration) && (
                  <FeatureCard
                    label="Enable Margin"
                    description="Allows borrowing against securities"
                    checked={formData.margin}
                    onChange={(e) => set("margin", e.target.checked)}
                  />
                )}
                <FeatureCard
                  label="Enable Options"
                  description="Trading options contracts"
                  checked={formData.options}
                  onChange={(e) => set("options", e.target.checked)}
                />
                {brokerageFlags.isWaverly && (
                  <FeatureCard
                    label="Planning Account"
                    description="Is this a Planning Account?"
                    checked={formData.is_planned !== false}
                    onChange={(e) => set("is_planned", e.target.checked)}
                  />
                )}
              </div>
            </div>
          </section>
          )}

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* 2. Contributions & Withdrawals — standard registrations only      */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          {!isSpecialReg && (
            <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-6">
              <SectionHeader num="2" color="purple">
                Contributions &amp; Withdrawals
              </SectionHeader>

            <div className="flex items-center justify-between bg-purple-50 p-3 rounded-lg">
              <p className="text-sm text-purple-800 font-medium">
                Do you want to add any?
              </p>
              <div className="flex bg-white rounded-lg p-1 border border-purple-200">
                <button
                  type="button"
                  onClick={() => setAddTransactions(true)}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${addTransactions ? "bg-purple-600 text-white shadow" : "text-gray-500 hover:bg-gray-100"}`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setAddTransactions(false)}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${!addTransactions ? "bg-purple-600 text-white shadow" : "text-gray-500 hover:bg-gray-100"}`}
                >
                  No
                </button>
              </div>
            </div>

            {addTransactions && (
              <div className="space-y-6">
                {/* Deposits */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-bold text-sm text-gray-700 uppercase tracking-wide">
                      Contributions
                    </h5>
                    <button
                      type="button"
                      onClick={() =>
                        setDeposits((d) => [
                          ...d,
                          {
                            amount: "",
                            frequency: "Monthly",
                            type: "",
                            method: "ACH",
                            start_date: "",
                          },
                        ])
                      }
                      className="text-purple-600 text-sm font-bold hover:underline"
                    >
                      + Add
                    </button>
                  </div>
                  {deposits.length === 0 ? (
                    <p className="text-sm text-gray-400 italic text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      No contributions added.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {deposits.map((d, i) => (
                        <div
                          key={i}
                          className="border border-gray-200 rounded-xl p-4"
                        >
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-sm font-semibold text-gray-700">
                              Contribution #{i + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setDeposits((ds) =>
                                  ds.filter((_, j) => j !== i),
                                )
                              }
                              className="text-xs text-red-400 hover:text-red-600"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={lbl}>Amount ($)</label>
                              <input
                                type="number"
                                min={0}
                                placeholder="0.00"
                                value={d.amount}
                                onChange={(e) =>
                                  setDeposits((ds) =>
                                    ds.map((x, j) =>
                                      j === i
                                        ? { ...x, amount: e.target.value }
                                        : x,
                                    ),
                                  )
                                }
                                onWheel={(e) => e.currentTarget.blur()}
                                className={inp}
                              />
                            </div>
                            <div>
                              <label className={lbl}>Frequency</label>
                              <Sel
                                value={d.frequency}
                                onChange={(e) =>
                                  setDeposits((ds) =>
                                    ds.map((x, j) =>
                                      j === i
                                        ? { ...x, frequency: e.target.value }
                                        : x,
                                    ),
                                  )
                                }
                                options={FREQUENCY_OPTIONS}
                              />
                            </div>
                            <div>
                              <label className={lbl}>Method</label>
                              <Sel
                                value={d.method}
                                onChange={(e) =>
                                  setDeposits((ds) =>
                                    ds.map((x, j) =>
                                      j === i
                                        ? { ...x, method: e.target.value }
                                        : x,
                                    ),
                                  )
                                }
                                options={DEPOSIT_METHOD_OPTIONS}
                              />
                            </div>
                            <div>
                              <label className={lbl}>Start Date</label>
                              <DateInput
                                value={d.start_date}
                                onChange={(v) =>
                                  setDeposits((ds) =>
                                    ds.map((x, j) =>
                                      j === i ? { ...x, start_date: v } : x,
                                    ),
                                  )
                                }
                                placeholder="mm-dd-yyyy"
                                className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Withdrawals */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-bold text-sm text-gray-700 uppercase tracking-wide">
                      Withdrawals
                    </h5>
                    <button
                      type="button"
                      onClick={() =>
                        setWithdrawals((w) => [
                          ...w,
                          {
                            amount: "",
                            frequency: "Monthly",
                            type: "",
                            method: "ACH",
                            start_date: "",
                          },
                        ])
                      }
                      className="text-rose-600 text-sm font-bold hover:underline"
                    >
                      + Add
                    </button>
                  </div>
                  {withdrawals.length === 0 ? (
                    <p className="text-sm text-gray-400 italic text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      No withdrawals added.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {withdrawals.map((w, i) => (
                        <div
                          key={i}
                          className="border border-gray-200 rounded-xl p-4"
                        >
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-sm font-semibold text-gray-700">
                              Withdrawal #{i + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setWithdrawals((ws) =>
                                  ws.filter((_, j) => j !== i),
                                )
                              }
                              className="text-xs text-red-400 hover:text-red-600"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={lbl}>Amount ($)</label>
                              <input
                                type="number"
                                min={0}
                                placeholder="0.00"
                                value={w.amount}
                                onChange={(e) =>
                                  setWithdrawals((ws) =>
                                    ws.map((x, j) =>
                                      j === i
                                        ? { ...x, amount: e.target.value }
                                        : x,
                                    ),
                                  )
                                }
                                onWheel={(e) => e.currentTarget.blur()}
                                className={inp}
                              />
                            </div>
                            <div>
                              <label className={lbl}>Frequency</label>
                              <Sel
                                value={w.frequency}
                                onChange={(e) =>
                                  setWithdrawals((ws) =>
                                    ws.map((x, j) =>
                                      j === i
                                        ? { ...x, frequency: e.target.value }
                                        : x,
                                    ),
                                  )
                                }
                                options={FREQUENCY_OPTIONS}
                              />
                            </div>
                            <div>
                              <label className={lbl}>Method</label>
                              <Sel
                                value={w.method}
                                onChange={(e) =>
                                  setWithdrawals((ws) =>
                                    ws.map((x, j) =>
                                      j === i
                                        ? { ...x, method: e.target.value }
                                        : x,
                                    ),
                                  )
                                }
                                options={WITHDRAWAL_METHOD_OPTIONS}
                              />
                            </div>
                            <div>
                              <label className={lbl}>Start Date</label>
                              <DateInput
                                value={w.start_date}
                                onChange={(v) =>
                                  setWithdrawals((ws) =>
                                    ws.map((x, j) =>
                                      j === i ? { ...x, start_date: v } : x,
                                    ),
                                  )
                                }
                                placeholder="mm-dd-yyyy"
                                className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
          )}

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* 3. Investment Strategy — standard registrations only               */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          {!isSpecialReg && (
            <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-6">
              <SectionHeader num="3" color="blue">
                Investment Strategy
              </SectionHeader>

            <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">
                Do you want to change household defaults for this account?
              </p>
              <div className="flex bg-white rounded-lg p-1 border border-blue-200">
                <button
                  type="button"
                  onClick={() => setUseHouseholdDefaults(false)}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${!useHouseholdDefaults ? "bg-blue-600 text-white shadow" : "text-gray-500 hover:bg-gray-100"}`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setUseHouseholdDefaults(true)}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${useHouseholdDefaults ? "bg-blue-600 text-white shadow" : "text-gray-500 hover:bg-gray-100"}`}
                >
                  No
                </button>
              </div>
            </div>

            {/* Fields — editable when override=Yes, read-only when using defaults */}
            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${useHouseholdDefaults ? "opacity-75" : ""}`}
            >
              {showAccountRiskDetails(brokerageFlags, { options: formData.options }) && (
              <>
              {/* Primary Investment Objective — full width */}
              <div className="md:col-span-2">
                <label className={lbl}>Primary Investment Objective</label>
                <Sel
                  value={rt.invest_objectives?.[0] || ""}
                  onChange={(e) => handleObjectiveChange(e.target.value)}
                  options={mergeSelectOptions(
                    PRIMARY_OBJECTIVE_OPTIONS,
                    rt.invest_objectives?.[0],
                  )}
                  placeholder="Select..."
                  disabled={useHouseholdDefaults}
                />
              </div>

              {/* Risk Tolerance */}
              <div>
                <label className={lbl}>Risk Tolerance</label>
                <Sel
                  value={rt.risk_tolerance}
                  onChange={(e) => setRt("risk_tolerance", e.target.value)}
                  options={mergeSelectOptions(
                    RISK_TOLERANCE_OPTIONS,
                    rt.risk_tolerance,
                  )}
                  placeholder="Select..."
                  disabled={useHouseholdDefaults}
                />
              </div>

              {/* Time Horizon */}
              <div>
                <label className={lbl}>Time Horizon</label>
                <Sel
                  value={rt.time_horizon_label}
                  onChange={(e) => setRt("time_horizon_label", e.target.value)}
                  options={mergeSelectOptions(
                    ACCOUNT_TIME_HORIZON_OPTIONS,
                    rt.time_horizon_label,
                  )}
                  placeholder="Select..."
                  disabled={useHouseholdDefaults}
                />
              </div>
              </>
              )}

              {/* Account Decision Making — full width */}
              <div className="md:col-span-2">
                <label className={lbl}>Account Decision Making</label>
                <Sel
                  value={rt.decision_making}
                  onChange={(e) => setRt("decision_making", e.target.value)}
                  options={mergeSelectOptions(
                    DECISION_MAKING_OPTIONS,
                    rt.decision_making,
                  )}
                  placeholder="Select..."
                  disabled={useHouseholdDefaults}
                />
              </div>

              {/* Source of Funds */}
              {showSourceOfFunds(brokerageFlags) && (
                <div>
                  <label className={lbl}>Source of Funds</label>
                  <Sel
                    value={
                      Array.isArray(rt.source_of_funds)
                        ? rt.source_of_funds[0] || ""
                        : rt.source_of_funds || ""
                    }
                    onChange={(e) =>
                      setRt(
                        "source_of_funds",
                        e.target.value ? [e.target.value] : [],
                      )
                    }
                    options={mergeSelectOptions(
                      SOURCE_OF_FUNDS_OPTIONS,
                      Array.isArray(rt.source_of_funds)
                        ? rt.source_of_funds[0]
                        : rt.source_of_funds,
                    )}
                    placeholder="Select..."
                    disabled={useHouseholdDefaults}
                  />
                </div>
              )}

              {/* Use of Funds */}
              {showUseOfFunds(brokerageFlags) && (
                <div>
                  <label className={lbl}>Use of Funds</label>
                  <Sel
                    value={rt.use_of_funds}
                    onChange={(e) => setRt("use_of_funds", e.target.value)}
                    options={mergeSelectOptions(
                      PRIMARY_USE_OPTIONS,
                      rt.use_of_funds,
                    )}
                    placeholder="Select..."
                    disabled={useHouseholdDefaults}
                  />
                </div>
              )}

              {/* Liquidity Needs */}
              {showLiquidityRequirements(brokerageFlags) && (
                <div>
                  <label className={lbl}>Liquidity Needs (Amount)</label>
                  <Sel
                    value={rt.liquidity_needs}
                    onChange={(e) => setRt("liquidity_needs", e.target.value)}
                    options={mergeSelectOptions(
                      LIQUIDITY_NEEDS_OPTIONS,
                      rt.liquidity_needs,
                    )}
                    placeholder="Select..."
                    disabled={useHouseholdDefaults}
                  />
                </div>
              )}

              {/* Liquidity Time Horizon */}
              {showLiquidityRequirements(brokerageFlags) && (
                <div>
                  <label className={lbl}>Liquidity Time Horizon (Years)</label>
                  <Sel
                    value={rt.liquidity_time_horizon}
                    onChange={(e) =>
                      setRt("liquidity_time_horizon", e.target.value)
                    }
                    options={mergeSelectOptions(
                      LIQUIDITY_TIME_HORIZON_OPTIONS,
                      rt.liquidity_time_horizon,
                    )}
                    placeholder="Select..."
                    disabled={useHouseholdDefaults}
                  />
                </div>
              )}
            </div>

            {useHouseholdDefaults && (
              <p className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                Using household defaults. Select <strong>Yes</strong> above to
                customize for this account.
              </p>
            )}
          </section>
          )}
        </div>
        {/* end scrollable body */}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-8 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-md shadow-emerald-100 disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creating…
              </>
            ) : (
              "Create Account"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAccountModal;
